"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createLeadAction,
  updateLeadStatusAction,
  updateLeadAction,
  deleteLeadAction,
} from "@/actions/lead-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  UserPlus,
  Search,
  Mail,
  Euro,
  Trash2,
  ExternalLink,
  Plus,
  ArrowRightLeft,
  ChevronRight,
  TrendingUp,
  Pencil,
} from "lucide-react";

interface LeadItem {
  id: string;
  name: string;
  status: string; // lead, negotiation, won, lost
  estimatedValue: string;
  contactEmail: string;
  createdAt: string;
  updatedAt: string;
}

interface LeadsClientProps {
  initialLeads: LeadItem[];
}

const DEFAULT_MOCK_LEADS: LeadItem[] = [
  {
    id: "lead_1",
    name: "Acme Corp Refactoring",
    status: "lead",
    estimatedValue: "15000",
    contactEmail: "sales@acme.com",
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "lead_2",
    name: "Globex SaaS Integration",
    status: "negotiation",
    estimatedValue: "42000",
    contactEmail: "p.scorpio@globex.co",
    createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "lead_3",
    name: "Initech Security Audit",
    status: "won",
    estimatedValue: "8500",
    contactEmail: "gibbons@initech.com",
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "lead_4",
    name: "Soylent Green Distribution",
    status: "lost",
    estimatedValue: "95000",
    contactEmail: "h.roth@soylent.com",
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "lead_5",
    name: "Hooli Cloud Migration",
    status: "negotiation",
    estimatedValue: "68000",
    contactEmail: "gavin@hooli.xyz",
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const COLUMNS = [
  { id: "lead", name: "Lead", color: "neutral", bg: "bg-card-muted/50" },
  {
    id: "negotiation",
    name: "In Trattativa",
    color: "warning",
    bg: "bg-warning-soft/10",
  },
  { id: "won", name: "Vinto", color: "success", bg: "bg-success-soft/10" },
  { id: "lost", name: "Perso", color: "error", bg: "bg-error-soft/10" },
];

export function LeadsClient({ initialLeads }: LeadsClientProps) {
  const router = useRouter();

  // Use DB leads if present, otherwise fallback to mock data
  const [leadsList, setLeadsList] = useState<LeadItem[]>(() => {
    return initialLeads.length > 0 ? initialLeads : DEFAULT_MOCK_LEADS;
  });

  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form State
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newStatus, setNewStatus] = useState<
    "lead" | "negotiation" | "won" | "lost"
  >("lead");
  const [newEstimatedValue, setNewEstimatedValue] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");

  // Drag and Drop States
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData("text/plain", id);
    setActiveDragId(id);
  };

  const handleDragEnd = () => {
    setActiveDragId(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault(); // Crucial to allow drops
    if (dragOverColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || activeDragId;
    setActiveDragId(null);
    setDragOverColumn(null);

    if (!id) return;

    const lead = leadsList.find((l) => l.id === id);
    if (lead && lead.status !== targetColumnId) {
      await handleStatusChange(id, targetColumnId);
    }
  };

  useEffect(() => {
    if (initialLeads.length > 0) {
      setLeadsList(initialLeads);
    } else {
      setLeadsList(DEFAULT_MOCK_LEADS);
    }
  }, [initialLeads]);

  const handleOpenModal = () => {
    setSelectedLeadId(null);
    setNewName("");
    setNewStatus("lead");
    setNewEstimatedValue("");
    setNewContactEmail("");
    setError("");
    setIsModalOpen(true);
  };

  const handleEditLead = (lead: LeadItem) => {
    setSelectedLeadId(lead.id);
    setNewName(lead.name);
    setNewStatus(lead.status as any);
    setNewEstimatedValue(lead.estimatedValue);
    setNewContactEmail(lead.contactEmail);
    setError("");
    setIsModalOpen(true);
  };

  const handleCardClick = (lead: LeadItem, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest("a") ||
      target.closest("button") ||
      target.closest("select") ||
      target.closest("input") ||
      target.closest("label")
    ) {
      return;
    }
    handleEditLead(lead);
  };

  const getLeadOwner = (leadId: string) => {
    switch (leadId) {
      case "lead_1":
        return { id: "agent_giulia", name: "Giulia Bianchi", avatar: "GB" };
      case "lead_2":
        return { id: "agent_elena", name: "Elena Neri", avatar: "EN" };
      case "lead_3":
        return { id: "agent_alessio", name: "Alessio Corrado", avatar: "AC" };
      case "lead_4":
        return { id: "agent_matteo", name: "Matteo Rossi", avatar: "MR" };
      case "lead_5":
        return { id: "agent_sofia", name: "Sofia Verga", avatar: "SV" };
      default:
        const index =
          leadId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
          5;
        const owners = [
          { id: "agent_giulia", name: "Giulia Bianchi", avatar: "GB" },
          { id: "agent_elena", name: "Elena Neri", avatar: "EN" },
          { id: "agent_alessio", name: "Alessio Corrado", avatar: "AC" },
          { id: "agent_matteo", name: "Matteo Rossi", avatar: "MR" },
          { id: "agent_sofia", name: "Sofia Verga", avatar: "SV" },
        ];
        return owners[index];
    }
  };

  const handleCreateLead = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newName.trim()) {
      setError("Il nome del lead è obbligatorio.");
      return;
    }
    if (
      !newEstimatedValue ||
      isNaN(Number(newEstimatedValue)) ||
      Number(newEstimatedValue) < 0
    ) {
      setError("Il valore stimato deve essere un numero valido.");
      return;
    }
    if (!newContactEmail.trim() || !newContactEmail.includes("@")) {
      setError("Inserire un indirizzo email valido.");
      return;
    }

    setLoading(true);

    try {
      if (selectedLeadId) {
        const res = await updateLeadAction(selectedLeadId, {
          name: newName,
          status: newStatus,
          estimatedValue: newEstimatedValue,
          contactEmail: newContactEmail,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsModalOpen(false);
          router.refresh();
          setLeadsList((prev) =>
            prev.map((lead) =>
              lead.id === selectedLeadId
                ? {
                    ...lead,
                    name: newName,
                    status: newStatus,
                    estimatedValue: newEstimatedValue,
                    contactEmail: newContactEmail,
                    updatedAt: new Date().toISOString(),
                  }
                : lead,
            ),
          );
        }
      } else {
        const res = await createLeadAction({
          name: newName,
          status: newStatus,
          estimatedValue: newEstimatedValue,
          contactEmail: newContactEmail,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsModalOpen(false);
          router.refresh();
          // If not connected to a live DB, simulate client-side update
          if (initialLeads.length === 0) {
            const newLead: LeadItem = {
              id: res.id || `lead_${Date.now()}`,
              name: newName,
              status: newStatus,
              estimatedValue: newEstimatedValue,
              contactEmail: newContactEmail,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            setLeadsList((prev) => [...prev, newLead]);
          }
        }
      }
    } catch (err: any) {
      setError(
        err.message || "Si è verificato un errore durante il salvataggio.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id: string, newStatusVal: any) => {
    try {
      const res = await updateLeadStatusAction(id, newStatusVal);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
        // Simulate client-side update
        setLeadsList((prev) =>
          prev.map((lead) =>
            lead.id === id
              ? {
                  ...lead,
                  status: newStatusVal,
                  updatedAt: new Date().toISOString(),
                }
              : lead,
          ),
        );
      }
    } catch (err: any) {
      alert("Errore nell'aggiornamento dello stato");
    }
  };

  const handleDeleteLead = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (!confirm("Sei sicuro di voler eliminare questo lead?")) return;

    try {
      const res = await deleteLeadAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
        // Simulate client-side update
        setLeadsList((prev) => prev.filter((lead) => lead.id !== id));
      }
    } catch (err: any) {
      alert("Errore durante l'eliminazione");
    }
  };

  const formatCurrency = (val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return "€0";
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const filteredLeads = leadsList.filter(
    (lead) =>
      lead.name.toLowerCase().includes(search.toLowerCase()) ||
      lead.contactEmail.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      {/* Header controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca lead per nome o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Button
          onClick={handleOpenModal}
          className="gap-1.5 shrink-0 w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          Nuovo Lead
        </Button>
      </div>

      {/* Kanban Board Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-start h-full">
        {COLUMNS.map((col) => {
          const colLeads = filteredLeads.filter((l) => l.status === col.id);
          const colValueSum = colLeads.reduce(
            (acc, curr) => acc + parseFloat(curr.estimatedValue || "0"),
            0,
          );

          return (
            <div
              key={col.id}
              onDragOver={(e) => handleDragOver(e, col.id)}
              onDragLeave={() => setDragOverColumn(null)}
              onDrop={(e) => handleDrop(e, col.id)}
              className={`flex flex-col gap-4 rounded-xl border transition-all p-4 ${col.bg} min-h-[500px] ${
                dragOverColumn === col.id
                  ? "border-primary-500/80 bg-primary-50/5 ring-2 ring-primary-500/10"
                  : "border-border/60"
              }`}
            >
              {/* Column Header */}
              <div className="flex items-center justify-between pb-2 border-b border-border/40">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-text-primary uppercase tracking-wider">
                    {col.name}
                  </span>
                  <Badge variant={col.color as any}>{colLeads.length}</Badge>
                </div>
                <span className="text-xs font-semibold text-text-secondary">
                  {formatCurrency(colValueSum.toString())}
                </span>
              </div>

              {/* Column Cards */}
              <div className="space-y-3 flex-1 overflow-y-auto max-h-[600px] pr-1">
                {colLeads.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 border border-dashed border-border/40 rounded-lg bg-card/20 text-center text-xs text-text-muted">
                    Nessun lead in questa fase
                  </div>
                ) : (
                  colLeads.map((lead) => {
                    const owner = getLeadOwner(lead.id);
                    return (
                      <Card
                        key={lead.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, lead.id)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => handleCardClick(lead, e)}
                        className={`relative hover:border-primary-500/50 hover:shadow-sm transition-all group cursor-pointer active:cursor-grabbing ${
                          activeDragId === lead.id
                            ? "opacity-45 scale-[0.98] border-dashed border-primary-500/40"
                            : ""
                        }`}
                      >
                        {/* Hover action overlay for Graph, Pencil, Trash */}
                        <div className="absolute top-2 right-2 flex items-center gap-1 bg-card/95 backdrop-blur-xs border border-border/50 rounded-lg p-1 shadow-xs opacity-0 group-hover:opacity-100 transition-all duration-200 z-10">
                          <Link
                            href={`/explore?focus=${lead.id}`}
                            title="Visualizza nel Grafo"
                            className="p-1 hover:bg-primary-500/10 text-text-secondary hover:text-primary-600 rounded-md transition-colors"
                          >
                            <TrendingUp className="h-3.5 w-3.5" />
                          </Link>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditLead(lead);
                            }}
                            title="Modifica"
                            className="p-1 hover:bg-primary-500/10 text-text-secondary hover:text-primary-600 rounded-md transition-colors"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleDeleteLead(lead.id, e)}
                            title="Elimina"
                            className="p-1 hover:bg-error-soft text-text-secondary hover:text-error rounded-md transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        <CardHeader className="p-3 pb-2 space-y-1">
                          <div className="flex justify-between items-start gap-2">
                            <Link
                              href={`/sales/leads/${lead.id}`}
                              className="block flex-1 min-w-0"
                            >
                              <h4 className="font-bold text-sm text-text-primary hover:text-primary-600 transition-colors truncate">
                                {lead.name}
                              </h4>
                            </Link>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3 pt-0 pb-3 space-y-2">
                          <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                            <Mail className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">
                              {lead.contactEmail}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-xs font-semibold text-text-primary">
                            <Euro className="h-3.5 w-3.5 text-success" />
                            <span>{formatCurrency(lead.estimatedValue)}</span>
                          </div>
                        </CardContent>
                        <CardFooter className="p-3 pt-2 border-t border-border/30 flex justify-between items-center gap-2">
                          <div className="flex flex-col gap-1 min-w-0">
                            <span className="text-[9px] text-text-muted">
                              Agg.{" "}
                              {new Date(lead.updatedAt).toLocaleDateString(
                                "it-IT",
                              )}
                            </span>
                            <Link
                              href={`/people/${owner.id}`}
                              className="flex items-center gap-1 text-[10px] text-text-secondary hover:text-primary-600 font-medium"
                              title={`Owner: ${owner.name}`}
                            >
                              <span className="h-4 w-4 shrink-0 rounded-full bg-primary-600 text-text-on-primary flex items-center justify-center font-bold text-[8px] select-none">
                                {owner.avatar}
                              </span>
                              <span className="truncate max-w-[65px]">
                                {owner.name}
                              </span>
                            </Link>
                          </div>

                          {/* Status Select for moving cards */}
                          <div className="relative shrink-0 flex items-center gap-1">
                            <ArrowRightLeft className="h-3 w-3 text-text-muted" />
                            <select
                              value={lead.status}
                              onChange={(e) =>
                                handleStatusChange(lead.id, e.target.value)
                              }
                              className="text-[10px] font-semibold text-text-secondary bg-transparent border-0 outline-none cursor-pointer focus:ring-0 pr-1 hover:text-primary-600"
                            >
                              <option value="lead">Lead</option>
                              <option value="negotiation">Trattativa</option>
                              <option value="won">Vinto</option>
                              <option value="lost">Perso</option>
                            </select>
                          </div>
                        </CardFooter>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Lead Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedLeadId ? "Modifica Lead" : "Aggiungi Nuovo Lead"}
            </DialogTitle>
            <DialogDescription>
              {selectedLeadId
                ? "Modifica le informazioni dell'opportunità di vendita."
                : "Crea una nuova opportunità di vendita nella pipeline commerciale."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateLead} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="lead-name">Nome Opportunità / Azienda</Label>
              <Input
                id="lead-name"
                placeholder="es. Airbnb Europe Migration"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="lead-value">Valore Stimato (€)</Label>
                <Input
                  id="lead-value"
                  type="number"
                  placeholder="es. 25000"
                  value={newEstimatedValue}
                  onChange={(e) => setNewEstimatedValue(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lead-status">Stato Pipeline</Label>
                <Select
                  id="lead-status"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="lead">Lead</option>
                  <option value="negotiation">
                    In Trattativa (Negotiation)
                  </option>
                  <option value="won">Vinto (Won)</option>
                  <option value="lost">Perso (Lost)</option>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="lead-email">Email di Contatto</Label>
              <Input
                id="lead-email"
                type="email"
                placeholder="es. client@domain.com"
                value={newContactEmail}
                onChange={(e) => setNewContactEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {selectedLeadId ? "Salva Modifiche" : "Crea Lead"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
